/**
 * useAgentConcurrency.ts
 *
 * Global singleton that tracks how many agent runs are currently active.
 * Uses a module-level Set so all ChatView instances share the same state.
 *
 * Limits:
 *   free plan  → 1 concurrent agent
 *   pro plan   → 5 concurrent agents
 */

import { useState, useEffect, useCallback } from 'react'

// ── Module-level shared state ─────────────────────────────────
const activeRunIds = new Set<string>()
type Listener = () => void
const listeners = new Set<Listener>()

function notify() {
  listeners.forEach(fn => fn())
}

export function registerAgentRun(runId: string) {
  activeRunIds.add(runId)
  notify()
}

export function unregisterAgentRun(runId: string) {
  activeRunIds.delete(runId)
  notify()
}

export function getActiveAgentCount(): number {
  return activeRunIds.size
}

// ── Hook ──────────────────────────────────────────────────────
export function useAgentConcurrency(plan: 'free' | 'pro' = 'free') {
  const [count, setCount] = useState(() => activeRunIds.size)

  useEffect(() => {
    const update = () => setCount(activeRunIds.size)
    listeners.add(update)
    return () => { listeners.delete(update) }
  }, [])

  const maxAgents = plan === 'pro' ? 5 : 1

  const canStartAgent = count < maxAgents

  const checkAndRegister = useCallback((runId: string): boolean => {
    if (activeRunIds.size >= maxAgents) return false
    registerAgentRun(runId)
    return true
  }, [maxAgents])

  return {
    activeCount: count,
    maxAgents,
    canStartAgent,
    checkAndRegister,
    registerAgentRun,
    unregisterAgentRun,
  }
}
